import { useEffect, useRef, useState } from "react";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { cn } from "@/lib/utils";
import {
  useConversations,
  useCreateConversation,
  useDeleteConversation,
  useRenameConversation,
} from "../hooks";
import { useAssistantDock } from "../store";

// Histórico privado do usuário, ordenado por lastMessageAt (server). Nova/reabrir/
// renomear inline/excluir (ConfirmDialog). `onPick` fecha a lista após escolher/criar.
export function ConversationList({ onPick }: { onPick: () => void }) {
  const q = useConversations();
  const activeId = useAssistantDock((s) => s.activeConversationId);
  const setActive = useAssistantDock((s) => s.setActiveConversation);
  const create = useCreateConversation();
  const rename = useRenameConversation();
  const del = useDeleteConversation();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Foca o input ao entrar em edição (sem autoFocus — regra jsx-a11y/no-autofocus).
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function reopen(id: string) {
    setActive(id);
    onPick();
  }
  function startNew() {
    create.mutate(undefined); // cria já e seleciona (onSuccess do hook)
    onPick();
  }
  async function remove(id: string, title: string | null) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: "Excluir conversa",
      description: `Excluir "${title ?? "Sem título"}"? Você não verá mais esta conversa.`,
      confirmLabel: "Excluir",
    });
    if (ok) del.mutate(id);
  }
  function submitRename(id: string) {
    if (draft.trim()) rename.mutate({ id, title: draft.trim() });
    setEditing(null);
  }

  if (q.isError)
    return (
      <div className="flex-1 overflow-y-auto p-3">
        <Notify.QueryError error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );

  return (
    <div className="flex-1 overflow-y-auto p-2">
      <Button variant="outline" size="sm" className="mb-2 w-full gap-1.5" onClick={startNew}>
        <Plus className="h-4 w-4" />
        Nova conversa
      </Button>
      {q.isLoading ? <div className="p-2 text-sm text-muted-foreground">Carregando…</div> : null}
      {q.data && q.data.length === 0 ? (
        <div className="p-2 text-sm text-muted-foreground">Nenhuma conversa ainda.</div>
      ) : null}
      <ul className="space-y-0.5">
        {(q.data ?? []).map((c) => (
          <li
            key={c.id}
            className={cn(
              "group flex items-center gap-1 rounded-md px-2 py-1.5",
              c.id === activeId ? "bg-muted" : "hover:bg-muted/60"
            )}
          >
            {editing === c.id ? (
              <>
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitRename(c.id);
                    if (e.key === "Escape") setEditing(null);
                  }}
                  maxLength={80}
                  aria-label="Novo título"
                  className="min-w-0 flex-1 rounded border border-input bg-background px-2 py-1 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Salvar"
                  onClick={() => submitRename(c.id)}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Cancelar"
                  onClick={() => setEditing(null)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => reopen(c.id)}
                  className="min-w-0 flex-1 truncate text-left text-sm text-foreground"
                >
                  {c.title ?? "Sem título"}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="opacity-0 group-hover:opacity-100"
                  aria-label="Renomear"
                  onClick={() => {
                    setEditing(c.id);
                    setDraft(c.title ?? "");
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive opacity-0 group-hover:opacity-100"
                  aria-label="Excluir"
                  onClick={() => void remove(c.id, c.title)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
